import * as React from 'react';
import styled from 'styled-components';
import gettext, { ngettext } from '@sdk/lang/gettext';
import Picker from '@sdk/components/forms/Picker';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import TextField from '@sdk/components/forms/TextField';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import Href from '@sdk/components/ui/Href';
import useConfig from '@sdk/config/useConfig';
import Padding from '@sdk/components/ui/spacing/Padding';
import useAuth from '@sdk/authentication/useAuth';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { Feature } from '@sdk/common/features';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';
import type { Sections } from '@sdk/components/ui/anchorMenu/createSections';

import type { $stateType } from '../common/types/AngularTypes';
import PremiumGuard from '../premiumGuard/PremiumGuard';
import usePremiumGuard from '../premiumGuard/usePremiumGuard';
import getPremiumTooltipId from '../premiumGuard/getPremiumTooltipId';
import { PremiumModalNames } from '../premiumGuard/constants/modalNames';

import CollapsibleSettingsSection from './components/CollapsibleSettingsSection';
import RowSpaceBetween from './components/RowSpaceBetween';
import SettingsHeader from './components/SettingsHeader';
import SettingsSection from './components/SettingsSection';
import SettingsSwitcherRow from './components/SettingsSwitcherRow';
import KioskSessionsTable from './components/kiosk/KioskSessionsTable';
import Geofencing from './components/timeclock/Geofencing';
import Attendance from './components/timeclock/Attendance';
import Restrictions from './components/timeclock/Restrictions';
import { useSettingsErrorsContext } from './SettingsErrorContext';
import { TabType } from './types';
import {
  ANY_PROTOCOL_REGEX,
  CLOCK_IN_ROUNDING_OPTIONS,
  FIELD_WIDTH_205,
  SET_UP_TIME_CLOCK_KIOSK_URL,
} from './constants';
import { useUserOrgSettings } from './UserOrgSettingsContext';

const StyledTextField = styled(TextField)`
  max-width: 205px;
`;

type Props = {
  personasDict: {
    [id in string]: AuthUserType;
  };
  sectionsConfig: Sections;
  $state: $stateType;
};

const TimeclockScreen: React.FC<React.PropsWithChildren<Props>> = ({
  personasDict,
  sectionsConfig,
  $state,
}) => {
  const { isOrgFeatureEnabled, currentUser } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { toggleError } = useSettingsErrorsContext();
  const config = useConfig();
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const {
    clockInEnabled,
    clockInFromWebAndApps,
    clockInFromKiosk,
    clockInRoundMinutes,
    usersCanEditTheirTimesheets,
    clockInAutoClockoutMinutes,
    clockInAutoClockoutEnabled,
  } = userOrgSettings;

  const validClockInAutoClockoutMinutes = (value: number | string): boolean => {
    return validateNumber(value, {
      [ValidateOptions.integer]: true,
      [ValidateOptions.validNumber]: ValidNumberOption.nonNegative,
    });
  };

  return (
    <>
      <SettingsHeader
        ref={sectionsConfig.refs.generalSettings}
        withSeparator
        title={gettext('General settings')}
      />

      <CollapsibleSettingsSection
        isActive={clockInEnabled && isOrgFeatureEnabled(Feature.clockIn)}
        headerItem={
          <>
            <SettingsSwitcherRow
              withSeparator
              disabled={!currentUser.isAdmin}
              value={clockInFromWebAndApps && clockInEnabled}
              onChange={(value): void =>
                onUserOrgSettingsChange({
                  clockInFromWebAndApps: value,
                  clockInEnabled: value || clockInFromKiosk,
                })
              }
              title={gettext('Enable mobile and web time clock')}
              subtitle={gettext(
                'Allow employees to clock in and out of their shifts from the Sling mobile or web apps.'
              )}
            />
            <PremiumGuard
              hasAccess={isOrgFeatureEnabled(Feature.kiosk)}
              premiumGuardModalName={PremiumModalNames.kiosk}
              tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.kiosk)]}>
              <SettingsSwitcherRow
                withSeparator
                disabled={!currentUser.isAdmin}
                value={clockInEnabled && clockInFromKiosk}
                onChange={(value): void =>
                  onUserOrgSettingsChange({
                    clockInFromKiosk: value,
                    clockInEnabled: value || clockInFromWebAndApps,
                  })
                }
                premiumGuard={getPremiumGuardConfig(Feature.kiosk)}
                title={gettext('Enable kiosk')}
                subtitleItem={
                  <>
                    <Text14 themeColor={ThemeColorType.gray}>
                      <TranslateWithComponents
                        replaceComponents={{
                          component: (
                            <Href
                              themeColor={ThemeColorType.primary}
                              fontSize={14}
                              target="_blank"
                              href={config.KIOSK_APP_URL}>
                              {config.KIOSK_APP_URL.replace(ANY_PROTOCOL_REGEX, '')}
                            </Href>
                          ),
                        }}>
                        {gettext(
                          `Allow employees to clock in and out of their shifts from designated terminals.
                        Create a time clock terminal by opening [[component]] on the device you wish to use.`
                        )}
                      </TranslateWithComponents>
                    </Text14>
                    <Padding top={20}>
                      <Href
                        underline
                        themeColor={ThemeColorType.gray}
                        fontSize={14}
                        target="_blank"
                        href={SET_UP_TIME_CLOCK_KIOSK_URL}>
                        {gettext('Learn More')}
                      </Href>
                    </Padding>
                  </>
                }
              />
            </PremiumGuard>
          </>
        }
        contentItem={
          <>
            {(clockInEnabled || clockInFromKiosk) && (
              <>
                <SettingsSection withSeparator>
                  <Column>
                    <RowSpaceBetween>
                      <Column>
                        <Text14 semiBold themeColor={ThemeColorType.gray}>
                          {gettext('Rounding')}
                        </Text14>
                        <Text14 themeColor={ThemeColorType.gray}>
                          {gettext('Round clock in and out times to the nearest increment.')}
                        </Text14>
                      </Column>
                      <Picker
                        disabled={!currentUser.isAdmin}
                        customWidth={FIELD_WIDTH_205}
                        options={CLOCK_IN_ROUNDING_OPTIONS}
                        value={clockInRoundMinutes}
                        onChange={(value: number): void =>
                          onUserOrgSettingsChange({ clockInRoundMinutes: value })
                        }
                      />
                    </RowSpaceBetween>
                  </Column>
                </SettingsSection>

                <CollapsibleSettingsSection
                  isActive={clockInAutoClockoutEnabled}
                  headerItem={
                    <SettingsSwitcherRow
                      pBottom={20}
                      disabled={!currentUser.isAdmin}
                      value={clockInAutoClockoutEnabled}
                      onChange={(value): void =>
                        onUserOrgSettingsChange({ clockInAutoClockoutEnabled: value })
                      }
                      title={gettext('Automatic clock out')}
                      subtitle={gettext(
                        'Automatically clock out employees after their scheduled shift ends.'
                      )}
                    />
                  }
                  contentItem={
                    <Column pBottom={20}>
                      <RowSpaceBetween>
                        <Text14 themeColor={ThemeColorType.gray}>
                          <TranslateWithComponents
                            replaceComponents={{
                              component: (
                                <Text14 themeColor={ThemeColorType.primary}>
                                  {clockInAutoClockoutMinutes}
                                </Text14>
                              ),
                            }}>
                            {ngettext(
                              'Clock out [[component]] minute after shift is scheduled to end.',
                              'Clock out [[component]] minutes after shift is scheduled to end.',
                              clockInAutoClockoutMinutes
                            )}
                          </TranslateWithComponents>
                        </Text14>
                        <StyledTextField
                          skipErrorMessage
                          maxLength={50}
                          disabled={!currentUser.isAdmin}
                          value={clockInAutoClockoutMinutes || 0}
                          postfix={gettext('Minutes')}
                          onChange={(e): void => {
                            onUserOrgSettingsChange({
                              clockInAutoClockoutMinutes: Number(e.currentTarget.value) || 0,
                            });
                            toggleError(
                              TabType.timeClock,
                              'clockInAutoClockoutMinutes',
                              !validClockInAutoClockoutMinutes(Number(e.currentTarget.value) || 0)
                            );
                          }}
                          invalid={!validClockInAutoClockoutMinutes(clockInAutoClockoutMinutes)}
                        />
                      </RowSpaceBetween>
                    </Column>
                  }
                />

                <PremiumGuard
                  hasAccess={isOrgFeatureEnabled(Feature.lateArrival)}
                  premiumGuardModalName={PremiumModalNames.lateArrival}
                  tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.lateArrival)]}>
                  <>
                    <SettingsHeader
                      ref={sectionsConfig.refs.attendance}
                      withSeparator
                      title={gettext('Attendance')}
                    />
                    <Attendance premiumGuard={getPremiumGuardConfig(Feature.lateArrival)} />
                  </>
                </PremiumGuard>

                <SettingsHeader
                  ref={sectionsConfig.refs.timesheets}
                  title={gettext('Timesheets')}
                />
                <SettingsSwitcherRow
                  withSeparator
                  disabled={!currentUser.isAdmin}
                  subtitle={gettext('Allow employees to edit their timesheets.')}
                  value={usersCanEditTheirTimesheets}
                  onChange={(value): void =>
                    onUserOrgSettingsChange({ usersCanEditTheirTimesheets: value })
                  }
                />

                <SettingsHeader
                  ref={sectionsConfig.refs.restrictions}
                  withSeparator
                  title={gettext('Restrictions')}
                />
                <Restrictions />

                {clockInFromWebAndApps && (
                  <>
                    <SettingsHeader
                      ref={sectionsConfig.refs.geofencing}
                      title={gettext('Geofencing')}
                    />
                    <Geofencing />
                  </>
                )}

                {clockInFromKiosk && (
                  <>
                    <SettingsHeader
                      ref={sectionsConfig.refs.kioskSessions}
                      withSeparator
                      title={gettext('Kiosk sessions')}
                    />
                    <SettingsSection pBottom={20}>
                      <KioskSessionsTable personasDict={personasDict} $state={$state} />
                    </SettingsSection>
                  </>
                )}
              </>
            )}
          </>
        }
      />
    </>
  );
};

export default TimeclockScreen;
